import React, { useMemo } from 'react';
import scaleLinear from '@visx/scale/lib/scales/linear';
import LinePath from '@visx/shape/lib/shapes/LinePath';
import { curveMonotoneX } from '@visx/curve';
import { useThemeColors } from '../../hooks/useThemeColors';
import { classifyShape } from '../../utils/analysis/trajectory';
import ChartA11y from './ChartA11y';

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Maps a shape classification to a Sparkline trend (the polarity-aware label
 * used for color selection). For risk scores in DriftAlarm, higher is worse
 * (PRD-51), so a 'rising' shape is 'worsening'.
 */
function shapeToTrend(shape) {
  if (shape === 'rising') return 'worsening';
  if (shape === 'falling') return 'improving';
  return 'stable';
}

export default function Sparkline({
  data,
  width = 120,
  height = 28,
  trend = 'auto',
  showDots = false,
  strokeWidth = 1.5,
  title,
  desc,
  className,
  style,
}) {
  const colors = useThemeColors();

  const series = useMemo(
    () => (Array.isArray(data) ? data.filter((x) => Number.isFinite(x)) : []),
    [data]
  );

  const resolvedTrend = useMemo(() => {
    if (trend && trend !== 'auto') return trend;
    return shapeToTrend(classifyShape(series));
  }, [trend, series]);

  const strokeColor =
    resolvedTrend === 'worsening'
      ? colors.severity.critical
      : resolvedTrend === 'improving'
      ? colors.risk.healthy
      : colors.text.muted;

  if (isDev && (!title || typeof title !== 'string')) {
    throw new Error('Sparkline: `title` prop is required for accessibility (see PRD-69 §3.2.0).');
  }

  if (series.length < 2) {
    return (
      <ChartA11y
        title={title}
        desc={desc || 'Not enough data to render a sparkline.'}
        width={width}
        height={height}
        className={className}
        style={style}
      >
        <line
          x1={0}
          x2={width}
          y1={height / 2}
          y2={height / 2}
          stroke={colors.text.muted}
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      </ChartA11y>
    );
  }

  const padY = Math.max(2, strokeWidth);
  const yValues = series;
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);

  const xScale = scaleLinear({
    domain: [0, series.length - 1],
    range: [0, width],
  });

  const yScale = scaleLinear({
    domain: yMin === yMax ? [yMin - 1, yMax + 1] : [yMin, yMax],
    range: [height - padY, padY],
  });

  return (
    <ChartA11y title={title} desc={desc} width={width} height={height} className={className} style={style}>
      <LinePath
        data={series}
        x={(_d, i) => xScale(i)}
        y={(d) => yScale(d)}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        curve={curveMonotoneX}
        fill="none"
      />
      {showDots
        ? series.map((d, i) => (
            <circle key={i} cx={xScale(i)} cy={yScale(d)} r={1.5} fill={strokeColor} />
          ))
        : null}
    </ChartA11y>
  );
}
